import React, { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import paths from 'routes/paths';

const BANNERS = [
    { id: 'banner-3anos-1', src: '/assets/images/promo/banner-3anos-1.png', alt: '3 anos de Whizr — 3 anos conectando pessoas, negócios e experiências', to: paths.promotions.threeYears },
    { id: 'banner-3anos-2', src: '/assets/images/promo/banner-3anos-2.png', alt: 'Desconto especial nas taxas: 6% no Pix e 8% no cartão', to: paths.promotions.threeYears },
];

const PromoBannersCarousel = () => {
    const navigate = useNavigate();
    const swiperRef = useRef(null);

    const prefersReducedMotion = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const handleNext = () => swiperRef.current?.slideNext();
    const handlePrev = () => swiperRef.current?.slidePrev();

    if (!BANNERS.length) return null;

    return (
        <CarouselWrapper>
            {BANNERS.length > 1 && (
                <>
                    <ArrowLeft onClick={handlePrev} aria-label="Banner anterior">
                        <ChevronLeft size={20} color={colors.night} />
                    </ArrowLeft>
                    <ArrowRight onClick={handleNext} aria-label="Próximo banner">
                        <ChevronRight size={20} color={colors.night} />
                    </ArrowRight>
                </>
            )}

            <StyledSwiper
                slidesPerView={1}
                loop={BANNERS.length > 1}
                autoplay={(BANNERS.length > 1 && !prefersReducedMotion) ? {
                    delay: 6000,
                    disableOnInteraction: true,
                    pauseOnMouseEnter: true,
                } : false}
                pagination={BANNERS.length > 1 ? { clickable: true } : false}
                onSwiper={(swiper) => { swiperRef.current = swiper; }}
                modules={[Autoplay, Pagination]}
            >
                {BANNERS.map((banner) => (
                    <SwiperSlide key={banner.id}>
                        <BannerButton
                            type="button"
                            onClick={() => navigate(banner.to)}
                        >
                            <BannerImage src={banner.src} alt={banner.alt} />
                        </BannerButton>
                    </SwiperSlide>
                ))}
            </StyledSwiper>
        </CarouselWrapper>
    );
};

const CarouselWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const StyledSwiper = styled(Swiper)`
    width: 100%;
    height: auto;
    border-radius: 16px;
    overflow: hidden;

    .swiper-pagination {
        position: absolute;
        bottom: 10px;
    }

    .swiper-pagination-bullet {
        background: ${colors.neutralLight};
        opacity: 0.6;
        width: 8px;
        height: 8px;
        margin: 0 5px;
        border-radius: 50%;
        transition: opacity 0.3s;
        cursor: pointer;
    }

    .swiper-pagination-bullet-active {
        opacity: 1;
    }
`;

const BannerButton = styled.button`
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
`;

const BannerImage = styled.img`
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 1920 / 520;
    object-fit: cover;
    border-radius: 16px;
`;

const ArrowLeft = styled(FullyCentered)`
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    transition: all ease-in-out .2s;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}22;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    z-index: 4;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
        border-color: ${colors.occasionalPurple};
        svg { stroke: ${colors.occasionalPurple}; }
    }

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        display: none;
    }
`;

const ArrowRight = styled(FullyCentered)`
    position: absolute;
    right: 22px;
    top: 50%;
    transform: translateY(-50%);
    transition: all ease-in-out .2s;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}22;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    z-index: 4;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
        border-color: ${colors.occasionalPurple};
        svg { stroke: ${colors.occasionalPurple}; }
    }

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        display: none;
    }
`;

export default PromoBannersCarousel;
